import { emitter, type EventSubscription } from '../events/bridge-events-emitter';
import type { AnyBridgeEvent } from '../events/bridge-events-types';

/* eslint-disable no-console */

/**
 * Configuration options for the debug consumer.
 */
export interface DebugConfig {
	/**
	 * Whether debug logging is enabled.
	 */
	enabled: boolean;

	/**
	 * Filter events by category or specific event types.
	 */
	eventFilter?: string[];

	/**
	 * Maximum number of events to keep in memory.
	 */
	maxHistory: number;
}

/**
 * Default debug configuration.
 */
const DEFAULT_CONFIG: DebugConfig = {
	enabled: false,
	maxHistory: 1000,
};

/**
 * Debug consumer class that formats and displays bridge API events.
 */
export class BridgeDebugConsumer {
	private config: DebugConfig = { ...DEFAULT_CONFIG };
	private subscription: EventSubscription | null = null;
	private eventHistory: AnyBridgeEvent[] = [];
	private clientColors = new Map<string, string>();
	private commandColors = new Map<string, string>();

	private recentErrors = new Map<string, number>();

	private readonly ERROR_DEBOUNCE_MS = 5000; // 5 seconds

	/**
	 * Color palette for consistent coloring.
	 */
	private readonly colors = [
		'#FF6B6B',
		'#4ECDC4',
		'#45B7D1',
		'#96CEB4',
		'#FFEAA7',
		'#DDA0DD',
		'#98D8C8',
		'#F7DC6F',
		'#BB8FCE',
		'#85C1E9',
	];

	/**
	 * Event type icons for visual distinction.
	 */
	private readonly eventIcons: Record<AnyBridgeEvent['type'], string> = {
		// Bridge events
		'bridge-ready': '🌉',
		'bridge-destroyed': '💥',

		// Client events
		'client-registered': '📝',
		'client-ready': '✅',
		'client-discovered': '🔍',
		'client-teardown': '🔄',

		// Command events
		'command-created': '⚡',
		'command-sent': '📤',
		'command-received': '📥',
		'command-completed': '✅',
		'command-failed': '❌',

		// Queue events
		'message-queued': '📋',
		'queue-flushed': '🚀',
		'queue-cleared': '🧹',
		'message-dropped': '🗑️',

		// Transport events
		'transport-ready': '🚛',
		'transport-message-sent': '📡',
		'transport-message-received': '📻',
		'transport-error': '⚠️',
		'transport-destroyed': '💀',

		// Handler events
		'handler-registered': '🔧',
		'handler-unregistered': '🔓',

		// Broadcast events
		'command-broadcast': '📢',
	};

	/**
	 * Start consuming and logging bridge API events.
	 */
	enable(): void {
		if (this.subscription) {
			return; // Already enabled
		}

		this.config.enabled = true;
		this.subscription = emitter().onAny((event) => {
			if (this.config.enabled && this.shouldLogEvent(event)) {
				this.logEvent(event);
				this.addToHistory(event);
			}
		});

		console.log(
			'%c🌉 Rovo Bridge API Debug Mode Enabled',
			'color: #4ECDC4; font-weight: bold; font-size: 14px;',
		);
	}

	/**
	 * Stop consuming bridge API events.
	 */
	disable(): void {
		if (this.subscription) {
			this.subscription.unsubscribe();
			this.subscription = null;
		}

		this.config.enabled = false;

		console.log(
			'%c🌉 Rovo Bridge API Debug Mode Disabled',
			'color: #FF6B6B; font-weight: bold; font-size: 14px;',
		);
	}

	/**
	 * Update debug configuration.
	 */
	configure(newConfig: Partial<DebugConfig>): void {
		this.config = { ...this.config, ...newConfig };

		console.log(
			'%c🔧 Debug configuration updated',
			'color: #96CEB4; font-weight: bold;',
			this.config,
		);
	}

	/**
	 * Get current configuration.
	 */
	getConfig(): DebugConfig {
		return { ...this.config };
	}

	/**
	 * Get event history.
	 */
	getHistory(): AnyBridgeEvent[] {
		return [...this.eventHistory];
	}

	/**
	 * Clear event history.
	 */
	clearHistory(): void {
		this.eventHistory = [];
		console.log('%c🧹 Event history cleared', 'color: #FFEAA7; font-weight: bold;');
	}

	/**
	 * Get debug statistics.
	 */
	getStats(): {
		enabled: boolean;
		totalEvents: number;
		eventCounts: Record<string, number>;
		uniqueClients: number;
		uniqueCommands: number;
	} {
		const eventCounts = this.eventHistory.reduce(
			(counts, event) => {
				counts[event.type] = (counts[event.type] || 0) + 1;
				return counts;
			},
			{} as Record<string, number>,
		);

		return {
			enabled: this.config.enabled,
			totalEvents: this.eventHistory.length,
			eventCounts,
			uniqueClients: new Set(
				this.eventHistory.filter((e): e is any => 'clientId' in e).map((e) => e.clientId),
			).size,
			uniqueCommands: new Set(
				this.eventHistory.filter((e): e is any => 'commandType' in e).map((e) => e.commandType),
			).size,
		};
	}

	/**
	 * Export event history as JSON.
	 */
	exportHistory(): string {
		return JSON.stringify(
			{
				exportedAt: new Date().toISOString(),
				config: this.config,
				events: this.eventHistory,
				stats: this.getStats(),
			},
			null,
			2,
		);
	}

	/**
	 * Log an event to the console with simple formatting.
	 */
	private logEvent(event: AnyBridgeEvent): void {
		const icon = this.eventIcons[event.type as keyof typeof this.eventIcons] || '📋';
		const timestamp = this.formatTimestamp(event.timestamp!);
		const bridgeId = event.bridgeId!;

		// Create collapsible group with basic info
		const groupTitle = `${icon} ${timestamp} ${event.type} [${bridgeId}]`;
		console.groupCollapsed(`%c${groupTitle}`, this.getEventTypeStyle(event.type));

		// Add key details in a simple format
		this.logKeyDetails(event);

		// Log raw event data
		console.log('%cRaw Event:', 'color: #888; font-style: italic;', event);
		console.groupEnd();
	}

	/**
	 * Log key event details in a simple format.
	 */
	private logKeyDetails(event: AnyBridgeEvent): void {
		// Client ID (if present)
		if ('clientId' in event) {
			console.log(
				`%c👤 Client: %c${(event as any).clientId}`,
				'font-weight: bold;',
				this.getClientStyle((event as any).clientId),
			);
		}

		// Command details (if present)
		if ('commandType' in event) {
			const cmdEvent = event as any;
			console.log(
				`%c⚡ Command: %c${cmdEvent.commandType}`,
				'font-weight: bold;',
				this.getCommandStyle(cmdEvent.commandType),
			);

			if (cmdEvent.senderId && cmdEvent.receiverId) {
				console.log(
					`%c📤 %c${cmdEvent.senderId} %c→ %c📥 %c${cmdEvent.receiverId}`,
					'font-weight: bold;',
					this.getClientStyle(cmdEvent.senderId),
					'color: #888;',
					'font-weight: bold;',
					this.getClientStyle(cmdEvent.receiverId),
				);
			}

			if (cmdEvent.duration !== undefined) {
				console.log(
					`%c⏱️ Duration: %c${this.formatDuration(cmdEvent.duration)}`,
					'font-weight: bold;',
					this.getDurationStyle(cmdEvent.duration),
				);
			}
		}

		// Error (if present)
		if ('error' in event && (event as any).error) {
			console.log('%c❌ Error:', 'color: #FF6B6B; font-weight: bold;', (event as any).error);
		}

		// Queue size (if present)
		if ('queueSize' in event) {
			console.log(
				`%c📋 Queue Size: %c${(event as any).queueSize}`,
				'font-weight: bold;',
				this.getQueueSizeStyle((event as any).queueSize),
			);
		}
	}

	/**
	 * Check if an event should be logged (with debouncing for transport errors).
	 */
	private shouldLogEvent(event: AnyBridgeEvent): boolean {
		// Apply event filter if configured
		if (this.config.eventFilter && this.config.eventFilter.length > 0) {
			const matchesFilter = this.config.eventFilter.some((filter) => {
				// Support category filters
				if (filter === 'commands') {
					return event.type.startsWith('command-');
				}
				if (filter === 'clients') {
					return event.type.startsWith('client-');
				}
				if (filter === 'transport') {
					return event.type.startsWith('transport-');
				}
				if (filter === 'queue') {
					return event.type.includes('queue') || event.type.includes('message-');
				}
				if (filter === 'handlers') {
					return event.type.startsWith('handler-');
				}
				if (filter === 'bridge') {
					return event.type.startsWith('bridge-');
				}
				if (filter === 'errors') {
					return (
						event.type.includes('error') ||
						event.type.includes('failed') ||
						event.type.includes('dropped')
					);
				}

				// Support exact event type matching
				return event.type === filter;
			});

			if (!matchesFilter) {
				return false;
			}
		}

		// Debounce transport errors to prevent spam during tick loops
		if (event.type === 'transport-error') {
			const transportEvent = event as any;
			const errorKey = `${event.type}-${transportEvent.messageId || 'unknown'}-${transportEvent.error?.message || 'unknown'}`;
			const lastLogged = this.recentErrors.get(errorKey) || 0;
			const now = Date.now();

			if (now - lastLogged < this.ERROR_DEBOUNCE_MS) {
				return false; // Skip this duplicate error
			}

			this.recentErrors.set(errorKey, now);
		}

		return true;
	}

	/**
	 * Add event to history with size management.
	 */
	private addToHistory(event: AnyBridgeEvent): void {
		this.eventHistory.push(event);

		// Trim history if it exceeds max size
		if (this.eventHistory.length > this.config.maxHistory) {
			this.eventHistory.shift();
		}
	}

	/**
	 * Get color for a client ID.
	 */
	private getClientColor(clientId: string): string {
		if (!this.clientColors.has(clientId)) {
			const colorIndex = this.clientColors.size % this.colors.length;
			this.clientColors.set(clientId, this.colors[colorIndex]);
		}
		return this.clientColors.get(clientId)!;
	}

	/**
	 * Get color for a command type.
	 */
	private getCommandColor(commandType: string): string {
		if (!this.commandColors.has(commandType)) {
			const colorIndex = this.commandColors.size % this.colors.length;
			this.commandColors.set(commandType, this.colors[colorIndex]);
		}
		return this.commandColors.get(commandType)!;
	}

	/**
	 * Get CSS style for event type.
	 */
	private getEventTypeStyle(eventType: string): string {
		const baseStyle = 'font-weight: bold; font-size: 12px;';

		if (eventType.includes('error') || eventType.includes('failed')) {
			return `${baseStyle} color: #FF6B6B;`;
		}
		if (eventType.includes('completed') || eventType.includes('ready')) {
			return `${baseStyle} color: #4ECDC4;`;
		}
		if (eventType.includes('created') || eventType.includes('sent')) {
			return `${baseStyle} color: #45B7D1;`;
		}

		return `${baseStyle} color: #666;`;
	}

	/**
	 * Get CSS style for client ID.
	 */
	private getClientStyle(clientId: string): string {
		return `color: ${this.getClientColor(clientId)}; font-weight: bold;`;
	}

	/**
	 * Get CSS style for command type.
	 */
	private getCommandStyle(commandType: string): string {
		return `color: ${this.getCommandColor(commandType)}; font-weight: bold;`;
	}

	/**
	 * Get CSS style for duration based on performance.
	 */
	private getDurationStyle(duration: number): string {
		if (duration < 10) {
			return 'color: #4ECDC4; font-weight: bold;';
		} // Fast
		if (duration < 100) {
			return 'color: #FFEAA7; font-weight: bold;';
		} // Medium
		return 'color: #FF6B6B; font-weight: bold;'; // Slow
	}

	/**
	 * Get CSS style for queue size.
	 */
	private getQueueSizeStyle(size: number): string {
		if (size === 0) {
			return 'color: #4ECDC4; font-weight: bold;';
		}
		if (size < 5) {
			return 'color: #FFEAA7; font-weight: bold;';
		}
		return 'color: #FF6B6B; font-weight: bold;';
	}

	/**
	 * Format timestamp for display.
	 */
	private formatTimestamp(timestamp: number): string {
		const date = new Date(timestamp);
		return date.toLocaleTimeString('en-US', { hour12: false });
	}

	/**
	 * Format duration in milliseconds.
	 */
	private formatDuration(ms: number): string {
		if (ms < 1) {
			return `${(ms * 1000).toFixed(0)}μs`;
		}
		if (ms < 1000) {
			return `${ms.toFixed(1)}ms`;
		}
		return `${(ms / 1000).toFixed(2)}s`;
	}
}

/**
 * Global debug consumer instance.
 */
let globalDebugConsumer: BridgeDebugConsumer | null = null;

/**
 * Get or create the global debug consumer.
 */
export function getDebugConsumer(): BridgeDebugConsumer {
	if (!globalDebugConsumer) {
		globalDebugConsumer = new BridgeDebugConsumer();
	}
	return globalDebugConsumer;
}

/**
 * Destroy the global debug consumer instance to prevent memory leaks.
 */
export function destroyGlobalDebugConsumer(): void {
	if (globalDebugConsumer) {
		globalDebugConsumer.disable();
		globalDebugConsumer = null;
	}
}
